import React from 'react'
import styled, { css } from 'styled-components'
import { Icon } from '~common/components/icon/icon'
import { Theme } from '~common/theme/types'

interface ExpandableSectionProps {
    title: string
    description: string
    expanded: boolean
    onToggle: () => void
    children: React.ReactNode
    icon?: React.ReactNode
    iconName?: string
    iconColor?: (props: { theme: Theme }) => string
    showTitleInSection?: boolean
}

export const ExpandableSection: React.FC<ExpandableSectionProps> = ({
    title,
    description,
    expanded,
    onToggle,
    children,
    icon,
    iconName,
    iconColor,
    showTitleInSection = true,
}) => {
    return (
        <Section>
            <SectionContainer onClick={onToggle} expanded={expanded}>
                <SectionHeader>
                    <SectionTitleContainer>
                        <IconContainer>
                            {icon ||
                                (iconName && (
                                    <Icon
                                        name={iconName}
                                        size="medium"
                                        iconColor={
                                            iconColor ||
                                            (({ theme }) =>
                                                theme.colors.text.primary)
                                        }
                                    />
                                ))}
                        </IconContainer>
                        {showTitleInSection && (
                            <SectionLabel>
                                <SectionTitle>{title}</SectionTitle>
                                <FoldedDescription>
                                    {description}
                                </FoldedDescription>
                            </SectionLabel>
                        )}
                    </SectionTitleContainer>
                    <Icon
                        name="triangle-down"
                        size="sm"
                        iconColor={({ theme }) => theme.colors.text.secondary}
                        style={{
                            transform: expanded
                                ? 'rotate(0deg)'
                                : 'rotate(-90deg)',
                            transition: 'transform 0.2s ease',
                        }}
                    />
                </SectionHeader>
                {expanded && (
                    <SectionContent onClick={(e) => e.stopPropagation()}>
                        {children}
                    </SectionContent>
                )}
            </SectionContainer>
        </Section>
    )
}

interface NonExpandableSectionProps {
    title?: string
    description?: string
    children: React.ReactNode
}

export const NonExpandableSection: React.FC<NonExpandableSectionProps> = ({
    title,
    description,
    children,
}) => {
    return (
        <Section>
            <SectionContentNoBorder>
                {title && (
                    <SectionHeader>
                        <SectionLabel>
                            <SectionTitle>{title}</SectionTitle>
                            {description && (
                                <FoldedDescription>
                                    {description}
                                </FoldedDescription>
                            )}
                        </SectionLabel>
                    </SectionHeader>
                )}
                {children}
            </SectionContentNoBorder>
        </Section>
    )
}

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[3]};
`

const SectionContainer = styled.div<{ expanded: boolean }>`
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    padding: ${({ theme }) => theme.spacing[2]};
    box-sizing: border-box;
    border: 1px solid ${({ theme }) => theme.colors.greyScale[300]};

    ${({ expanded }) =>
        !expanded &&
        css`
            &:hover {
                background-color: ${({ theme }) => theme.colors.background.bg2};
            }
        `}
`

const SectionHeader = styled.div`
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: ${({ theme }) => theme.spacing[8]};
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
    box-sizing: border-box;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    transition: background-color ${({ theme }) => theme.transitions.fast};
`

const IconContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[4]};
    margin-top: ${({ theme }) => theme.spacing[1]};
`
const SectionTitleContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[4]};
`

const SectionLabel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[1]};
`

const SectionTitle = styled.span`
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const FoldedDescription = styled.span`
    color: ${({ theme }) => theme.colors.text.secondary};
    margin-top: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
`

const SectionContent = styled.div`
    padding: ${({ theme }) => theme.spacing[4]};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    border-top: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    width: 100%;
`

const SectionContentNoBorder = styled.div`
    padding: ${({ theme }) => theme.spacing[4]};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    width: 100%;
`
